/**
 * Branded vault portal detection.
 *
 * A branded portal is served at `{slug}.{VITE_VAULT_PORTAL_BASE_DOMAIN}`. When the
 * current host matches that shape, the SPA scopes itself to the vault client/trustee
 * experience and applies the org's branding (see PortalProvider).
 *
 * The apex (`{base-domain}`) and the advisor app host are NOT portals.
 */

const BASE_DOMAIN = (
  import.meta.env.VITE_VAULT_PORTAL_BASE_DOMAIN || ""
).toLowerCase();

/** Labels that can never be an org slug; also kept in sync with the backend blocklist. */
const RESERVED_LABELS = new Set([
  "www",
  "app",
  "api",
  "admin",
  "mail",
  "vault",
  "portal",
  "support",
  "help",
  "status",
  "dashboard",
  "auth",
  "login",
  "signin",
  "signup",
  "register",
  "static",
  "assets",
  "cdn",
  "blog",
  "docs",
  "dev",
  "staging",
  "test",
  "internal",
  "secure",
  "securevault",
]);

/**
 * Returns the portal slug for the given hostname, or `null` when the host is not a
 * branded portal (no base domain configured, apex host, reserved label, or a host
 * outside the base domain).
 */
export function getPortalSlug(
  hostname: string = window.location.hostname
): string | null {
  if (!BASE_DOMAIN) return null;

  const host = hostname.toLowerCase().split(":")[0];
  if (host === BASE_DOMAIN) return null;

  const suffix = `.${BASE_DOMAIN}`;
  if (!host.endsWith(suffix)) return null;

  const label = host.slice(0, -suffix.length);
  // Single leading label only (reject `a.b.{base}`); reject reserved labels.
  if (!label || label.includes(".") || RESERVED_LABELS.has(label)) return null;

  return label;
}

/** True when the current host is a branded vault portal. */
export function isPortalHost(hostname?: string): boolean {
  return getPortalSlug(hostname) !== null;
}
